import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import KPITabNavigation from '../../../components/HR/KPITabNavigation';
import {
  fetchKpiSubmissions,
  updateKpiEmployeeSettings
} from '../../../services/kpiWorksheetService';
import api from '../../../services/api';
import { getEmployeeCategoryLabel, normalizeEmployeeCategory } from '../../../utils/employeeCategory';

const MONTHS = [
  { v: 1, label: 'January' },
  { v: 2, label: 'February' },
  { v: 3, label: 'March' },
  { v: 4, label: 'April' },
  { v: 5, label: 'May' },
  { v: 6, label: 'June' },
  { v: 7, label: 'July' },
  { v: 8, label: 'August' },
  { v: 9, label: 'September' },
  { v: 10, label: 'October' },
  { v: 11, label: 'November' },
  { v: 12, label: 'December' }
];

const STATUS_META = {
  submitted: { label: 'Submitted', color: 'success' },
  manager_reviewed: { label: 'Manager reviewed', color: 'primary' },
  draft: { label: 'Draft', color: 'warning' },
  not_started: { label: 'Not started', color: 'default' }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-PK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

const employeeName = (employee) =>
  [employee?.firstName, employee?.lastName].filter(Boolean).join(' ').trim() ||
  employee?.employeeId ||
  '—';

const formatEmpOption = (e) =>
  `${[e.firstName, e.lastName].filter(Boolean).join(' ') || e.employeeId || 'Employee'}${
    e.employeeId ? ` (${e.employeeId})` : ''
  }`;

/** Default KPI period = previous calendar month (Oct → Sep, Jan → Dec prior year). */
const getDefaultKpiPeriod = (d = new Date()) => {
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
};

const KPISubmissionsOverview = () => {
  const [year, setYear] = useState(() => getDefaultKpiPeriod().year);
  const [month, setMonth] = useState(() => getDefaultKpiPeriod().month);
  const [projectId, setProjectId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [search, setSearch] = useState('');
  const [submittedOnly, setSubmittedOnly] = useState(true);
  const [categoryTab, setCategoryTab] = useState('blue_collar'); // blue_collar | white_collar | all
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [reportingSearchLoading, setReportingSearchLoading] = useState(false);
  const [savingById, setSavingById] = useState({});
  const reportingSearchTimerRef = useRef(null);
  const reportingSearchSeqRef = useRef(0);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const { year: y, month: m } = getDefaultKpiPeriod();
        const res = await fetchKpiSubmissions({
          year: y,
          month: m,
          submittedOnly: false
        });
        const filterOptions = res.data?.data?.filterOptions || {};
        setProjects(Array.isArray(filterOptions.projects) ? filterOptions.projects : []);
        setDepartments(Array.isArray(filterOptions.departments) ? filterOptions.departments : []);
      } catch {
        setProjects([]);
        setDepartments([]);
      }
    };
    loadFilters();
  }, []);

  useEffect(
    () => () => {
      if (reportingSearchTimerRef.current) clearTimeout(reportingSearchTimerRef.current);
    },
    []
  );

  /** Search-as-you-type: no full employee dump; results only after typing name/ID. */
  const searchReportingEmployees = useCallback((query) => {
    const q = String(query || '').trim();
    if (reportingSearchTimerRef.current) clearTimeout(reportingSearchTimerRef.current);

    if (!q) {
      reportingSearchSeqRef.current += 1;
      setReportingOptions([]);
      setReportingSearchLoading(false);
      return;
    }

    setReportingSearchLoading(true);
    const seq = ++reportingSearchSeqRef.current;
    reportingSearchTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/hr/employees', {
          params: {
            search: q,
            active: true,
            limit: 50,
            page: 1
          }
        });
        if (seq !== reportingSearchSeqRef.current) return;
        const list = res.data?.data || res.data?.employees || res.data || [];
        setReportingOptions(Array.isArray(list) ? list : []);
      } catch {
        if (seq !== reportingSearchSeqRef.current) return;
        setReportingOptions([]);
      } finally {
        if (seq === reportingSearchSeqRef.current) {
          setReportingSearchLoading(false);
        }
      }
    }, 300);
  }, []);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchKpiSubmissions({
        year,
        month,
        projectId: projectId || undefined,
        departmentId: departmentId || undefined,
        submittedOnly,
        search: search.trim() || undefined,
        employeeCategory: categoryTab === 'all' ? undefined : categoryTab
      });
      setSummary(res.data?.data?.summary || null);
      setGroups(res.data?.data?.groups || []);
    } catch (err) {
      setSummary(null);
      setGroups([]);
      setError(err.response?.data?.message || 'Failed to load KPI submissions');
    } finally {
      setLoading(false);
    }
  }, [year, month, projectId, departmentId, submittedOnly, search, categoryTab]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const totalShown = useMemo(
    () => groups.reduce(
      (sum, group) => sum + group.departments.reduce((deptSum, dept) => deptSum + dept.employees.length, 0),
      0
    ),
    [groups]
  );

  const patchLocalEmployee = useCallback((employeeId, patch) => {
    const id = String(employeeId);
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        departments: group.departments.map((dept) => ({
          ...dept,
          employees: dept.employees.map((row) => {
            if (String(row.employee?._id) !== id) return row;
            const nextCategory =
              patch.employeeCategory != null
                ? normalizeEmployeeCategory(patch.employeeCategory) || row.employeeCategory
                : row.employeeCategory;
            const nextRlId =
              patch.reportingLineId !== undefined
                ? patch.reportingLineId
                : row.employee?.reportingLineId;
            const nextRlName =
              patch.reportingLineName !== undefined
                ? patch.reportingLineName
                : row.employee?.reportingLine;
            return {
              ...row,
              employeeCategory: nextCategory,
              employee: {
                ...row.employee,
                employeeCategory: nextCategory,
                reportingLineId: nextRlId,
                reportingLine: nextRlName || '—'
              }
            };
          })
        }))
      }))
    );
  }, []);

  const handleSaveRowSettings = async (row, { employeeCategory, reportingLineId }) => {
    const empId = row.employee?._id;
    if (!empId) return;

    const nextCategory =
      normalizeEmployeeCategory(employeeCategory) ||
      normalizeEmployeeCategory(row.employee?.employeeCategory) ||
      'white_collar';
    const nextRlId =
      reportingLineId !== undefined
        ? reportingLineId
        : row.employee?.reportingLineId || '';

    const prevCategory = normalizeEmployeeCategory(row.employee?.employeeCategory) || 'white_collar';
    const prevRlId = row.employee?.reportingLineId || '';
    if (nextCategory === prevCategory && String(nextRlId || '') === String(prevRlId || '')) {
      return;
    }

    const rlOpt = reportingOptions.find((e) => String(e._id) === String(nextRlId || ''));
    const rlName = rlOpt
      ? [rlOpt.firstName, rlOpt.lastName].filter(Boolean).join(' ').trim()
      : nextRlId
        ? row.employee?.reportingLine
        : '—';

    // Optimistic UI
    patchLocalEmployee(empId, {
      employeeCategory: nextCategory,
      reportingLineId: nextRlId || null,
      reportingLineName: rlName || '—'
    });

    setSavingById((prev) => ({ ...prev, [empId]: true }));
    try {
      await updateKpiEmployeeSettings(empId, {
        employeeCategory: nextCategory,
        reportingLine: nextRlId || null,
        year,
        month
      });
      toast.success(
        `${employeeName(row.employee)} → ${getEmployeeCategoryLabel(nextCategory)}${
          rlName && rlName !== '—' ? `, RL: ${rlName}` : ''
        }`
      );
      // If filtered by category and employee moved tabs, refresh so they leave this list
      if (categoryTab !== 'all' && categoryTab !== nextCategory) {
        await loadSubmissions();
      }
    } catch (err) {
      // Revert
      patchLocalEmployee(empId, {
        employeeCategory: prevCategory,
        reportingLineId: prevRlId || null,
        reportingLineName: row.employee?.reportingLine || '—'
      });
      toast.error(err.response?.data?.message || 'Failed to update employee settings');
    } finally {
      setSavingById((prev) => {
        const next = { ...prev };
        delete next[empId];
        return next;
      });
    }
  };

  const handleExportExcel = () => {
    const data = [];
    groups.forEach((group) => {
      const projectName = group.project?.name || 'Unassigned Project';
      group.departments.forEach((dept) => {
        const deptName = dept.department?.name || 'Unassigned Department';
        dept.employees.forEach((empRow) => {
          const commonInfo = {
            Project: projectName,
            Department: deptName,
            'Employee ID': empRow.employee?.employeeId || '—',
            Employee: employeeName(empRow.employee),
            Designation: empRow.employee?.designation || '—',
            Category: getEmployeeCategoryLabel(empRow.employee?.employeeCategory || empRow.employeeCategory),
            'Reporting Line': empRow.employee?.reportingLine || '—'
          };

          if (empRow.rows && empRow.rows.length > 0) {
            empRow.rows.forEach((kpi) => {
              data.push({
                ...commonInfo,
                'KPI Area': kpi.kpiArea || '—',
                Weightage: kpi.weight != null ? Number(kpi.weight).toFixed(2) : '—',
                Score: kpi.score1to5 != null ? Number(kpi.score1to5).toFixed(2) : '—',
                'Final Weightage':
                  kpi.finalWeightage != null ? Number(kpi.finalWeightage).toFixed(2) : '—'
              });
            });
          } else {
            data.push({
              ...commonInfo,
              'KPI Area': '—',
              Weightage: '—',
              Score: '—',
              'Final Weightage': '—'
            });
          }
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'KPI Submissions');
    XLSX.writeFile(workbook, `KPI_Submissions_${year}_${month}.xlsx`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <KPITabNavigation />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            KPI Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Turn off &quot;Show submitted only&quot; to see all staff by department. Change Blue/White collar and
            reporting line in the table — updates also apply in Employees.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportExcel}
          disabled={groups.length === 0}
        >
          Export to Excel
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs
            value={categoryTab}
            onChange={(_, v) => setCategoryTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="blue_collar"
              label={`Blue collar${summary?.blueCollar != null ? ` (${summary.blueCollar})` : ''}`}
            />
            <Tab
              value="white_collar"
              label={`White collar${summary?.whiteCollar != null ? ` (${summary.whiteCollar})` : ''}`}
            />
            <Tab value="all" label="All" />
          </Tabs>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
            <TextField
              select
              label="Month"
              size="small"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              sx={{ minWidth: 140 }}
            >
              {MONTHS.map((item) => (
                <MenuItem key={item.v} value={item.v}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              size="small"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ minWidth: 110 }}
            >
              {[0, 1, 2].map((offset) => {
                const y = new Date().getFullYear() - offset;
                return (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              label="Project"
              size="small"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All projects</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.name || project.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Department"
              size="small"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All departments</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department._id} value={department._id}>
                  {department.name || department.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Search"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Employee, project, department"
              sx={{ minWidth: 240, flex: 1 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={submittedOnly}
                  onChange={(e) => setSubmittedOnly(e.target.checked)}
                />
              }
              label="Show submitted only"
            />
            {summary && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Submitted: ${summary.submitted}`} color="success" variant="outlined" />
                <Chip
                  size="small"
                  label={`Manager reviewed: ${summary.managerReviewed}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip size="small" label={`Draft: ${summary.draft}`} color="warning" variant="outlined" />
                <Chip size="small" label={`Not started: ${summary.notStarted}`} variant="outlined" />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : totalShown === 0 ? (
        <Alert severity="info">No KPI records found for the selected period and filters.</Alert>
      ) : (
        <Stack spacing={2}>
          {groups.map((group) => (
            <Accordion key={group.project?._id || group.project?.name} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={700}>{group.project?.name || 'Unassigned project'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {group.departments.reduce((sum, dept) => sum + dept.employees.length, 0)} employee(s)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  {group.departments.map((dept) => (
                    <Box key={dept.department?._id || dept.department?.name}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {dept.department?.name || 'Unassigned department'}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({dept.employees.length})
                        </Typography>
                      </Typography>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee ID</TableCell>
                              <TableCell>Employee</TableCell>
                              <TableCell>Designation</TableCell>
                              <TableCell sx={{ minWidth: 150 }}>Category</TableCell>
                              <TableCell sx={{ minWidth: 220 }}>Reporting Line</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Total KPI score</TableCell>
                              <TableCell align="right">Weight %</TableCell>
                              <TableCell>Last saved</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dept.employees.map((row) => {
                              const statusMeta = STATUS_META[row.status] || STATUS_META.not_started;
                              const empId = row.employee?._id;
                              const saving = Boolean(savingById[empId]);
                              const categoryValue =
                                normalizeEmployeeCategory(row.employee?.employeeCategory) ||
                                normalizeEmployeeCategory(row.employeeCategory) ||
                                'white_collar';
                              const rlValue = row.employee?.reportingLineId || '';

                              return (
                                <TableRow key={empId}>
                                  <TableCell>{row.employee?.employeeId || '—'}</TableCell>
                                  <TableCell>{employeeName(row.employee)}</TableCell>
                                  <TableCell>{row.employee?.designation || '—'}</TableCell>
                                  <TableCell>
                                    <TextField
                                      select
                                      size="small"
                                      fullWidth
                                      value={categoryValue}
                                      disabled={saving}
                                      onChange={(e) =>
                                        handleSaveRowSettings(row, {
                                          employeeCategory: e.target.value,
                                          reportingLineId: rlValue
                                        })
                                      }
                                    >
                                      <MenuItem value="blue_collar">Blue Collar</MenuItem>
                                      <MenuItem value="white_collar">White Collar</MenuItem>
                                    </TextField>
                                  </TableCell>
                                  <TableCell>
                                    <Autocomplete
                                      size="small"
                                      fullWidth
                                      filterOptions={(x) => x}
                                      options={reportingOptions.filter(
                                        (e) => String(e._id) !== String(empId)
                                      )}
                                      value={
                                        reportingOptions.find(
                                          (e) => String(e._id) === String(rlValue)
                                        ) ||
                                        (rlValue
                                          ? {
                                              _id: rlValue,
                                              firstName: row.employee?.reportingLine || '',
                                              lastName: '',
                                              employeeId: ''
                                            }
                                          : null)
                                      }
                                      getOptionLabel={(option) =>
                                        option ? formatEmpOption(option) : ''
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        String(option?._id || '') === String(value?._id || '')
                                      }
                                      loading={reportingSearchLoading}
                                      disabled={saving}
                                      noOptionsText={
                                        reportingSearchLoading
                                          ? 'Searching…'
                                          : 'Type name or employee ID'
                                      }
                                      onInputChange={(_event, newInputValue, reason) => {
                                        if (reason === 'input') {
                                          searchReportingEmployees(newInputValue);
                                        }
                                        if (reason === 'clear') {
                                          searchReportingEmployees('');
                                        }
                                      }}
                                      onChange={(_, selected) => {
                                        if (selected?._id) {
                                          setReportingOptions((prev) => {
                                            if (prev.some((e) => String(e._id) === String(selected._id))) {
                                              return prev;
                                            }
                                            return [selected, ...prev];
                                          });
                                        }
                                        handleSaveRowSettings(row, {
                                          employeeCategory: categoryValue,
                                          reportingLineId: selected?._id || ''
                                        });
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Search name or ID"
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {reportingSearchLoading ? (
                                                  <CircularProgress color="inherit" size={16} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            )
                                          }}
                                        />
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={statusMeta.label}
                                      color={statusMeta.color}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.totalKPIScore != null
                                      ? Number(row.totalKPIScore).toFixed(2)
                                      : '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.totalWeight != null ? Number(row.totalWeight).toFixed(0) : '—'}
                                  </TableCell>
                                  <TableCell>{formatDateTime(row.lastSavedAt)}</TableCell>
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                      {saving && <CircularProgress size={16} />}
                                      <Button
                                        component={RouterLink}
                                        to={`/hr/kpi/sheet?employeeId=${empId}&year=${year}&month=${month}`}
                                        size="small"
                                        endIcon={<OpenInNewIcon />}
                                      >
                                        Open sheet
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default KPISubmissionsOverview;
